import { Divider, Drawer, Grid, Typography } from '@material-ui/core';
import { CadastroNotaComercialApiRequest, InstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest, FuncaoParticipante, GrupoAcesso, Perfil } from 'common/genericTypes';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, MdSpace, SmSpace, TextField } from 'm2m.components';
import { Dispatch, SetStateAction, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import NotaComercialParser from 'utils/parsers/notaComercialParser';
import { addAlert } from 'store/modules/snackBar';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';

export function FormularioDrawerEdicao({
  open,
  setOpen,
  handleSubmit,
  codigo,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleSubmit: () => void;
  codigo: string;
}) {
  const dispatch = useDispatch();
  const [documento, setDocumento] = useState<File | undefined>();
  const [justificativa, setJustificativa] = useState<string>('');

  const caracteristicas = useSelector((state: RootState) => state.notaComercialCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.notaComercialCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.notaComercialCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.notaComercialCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.notaComercialCadastro.emissor);
  const notaComercialId = useSelector((state: RootState) => state.notaComercialCadastro.caracteristicas.id);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);
  const isUsuarioCadastroOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes);
  const isParticipanteCustodiante = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante);
  const isUsuarioOperacoesMaster = useSelector(
    (state: RootState) =>
      state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes &&
      state.auth.usuario.participante.perfil === Perfil.Master);
  const notaComercialApi = useSelector((state: RootState) => state.notaComercialApi);

  const handleUpdateFileToStore = (attachments: Attachment[]): void => {
    const newAttachment = attachments?.[0].file;
    setDocumento(newAttachment);
  };

  const submitPutNotaComercialEdicao = async (
    notaComercialId: string,
    notaComercial: CadastroNotaComercialApiRequest,
  ): Promise<string> => {
    try {
      if (justificativa) {
        const result = await notaComercialApi.putEditarNotaComercial(
          dispatch,
          notaComercialId,
          notaComercial,
          justificativa,
          documento,
          true
        );
        return result.data;
      } else throw 'O campo de justificativa é obrigatório';
    } catch (error: any) {
      console.log(error);
      dispatch(addAlert({ message: error, severity: 'error' }));
    }
    return codigo;
  };

    const submitNotaComercialDocument = async (
      notaComercialId: string,
      emissionType: string,
      file: File
    ): Promise<UploadDocResponse[]> => {
      try {
        return notaComercialApi.putUploadDoc(dispatch, notaComercialId, emissionType, file, true);
      } catch (error) {
        return [];
      }
    };

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.NotaComercial,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  const handleEdicaoSubmit = async () => {
    try {
      if (caracteristicas.numeroEmissao && caracteristicas.numeroEmissaoOriginal && 
          Number(caracteristicas.numeroEmissao) !== Number(caracteristicas.numeroEmissaoOriginal)) {
        const numeroEmissaoReservado = await reservarNumeroEmissao();
        if (!numeroEmissaoReservado) return;
      }
      const notaComercial: CadastroNotaComercialApiRequest = NotaComercialParser.stateToCadastroNotaComercialApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );
      if (!notaComercialId) throw 'Erro ao editar a Nota Comercial.';

      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          const uploadResponse = await submitNotaComercialDocument(notaComercialId, document.type, document.file);
          if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
        }
      }
      
      if (isUsuarioOperacoesMaster)
        {
          if (caracteristicas.id) {
            for (const documentToDelete of uploadDocumentos.documentsToDelete) {
              documentToDelete.remoteFileInfo?.id &&
              (await notaComercialApi.deleteUploadDoc(dispatch, notaComercialId, documentToDelete.remoteFileInfo.id, true));
            }
          }
        }

      const result = await submitPutNotaComercialEdicao(notaComercialId, notaComercial);
      if (!result) throw 'Erro ao editar a Nota Comercial';

      dispatch(addAlert({ message: `Instrumento Financeiro ${result} editado com sucesso.`, severity: 'success' }));
      handleSubmit();

    } catch (error: any) {
      dispatch(addAlert({ message: error.message, severity: 'error' }));
      throw error;
    }
  };

  const handleClose = () => {
    setDocumento(undefined);
    setJustificativa('');
    setOpen(false);
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ style: { padding: '40px', maxWidth: 460 } }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h3">{'Edição'}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <MdSpace />
          <Typography variant="body2">{`Tem certeza que quer editar o instrumento financeiro ${codigo}?`}</Typography>
          <SmSpace />
          <TextField
            value={justificativa}
            label="Justificativa"
            onChange={(value) => setJustificativa(value)}
            multiline
            rows={4}
            required
          />
          <SmSpace />
          <UploadDocs
            multipleFiles={false}
            inputAttachments={[]}
            onChange={handleUpdateFileToStore}
          />
          <MdSpace />
          <Button
            onClick={handleEdicaoSubmit}
            disabled={!justificativa}
            variant="contained"
            color="primary"
            fullWidth
          >
            {'Confirmar Edição'}
          </Button>
          <SmSpace />
          <Button onClick={handleClose} variant="contained" color="error" fullWidth>
            {'Cancelar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
}
